import * as React from 'react';
import { Button } from '../../../Components/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../Components/Select';
import { Switch } from '../../../Components/Switch';
import { DiscountType } from '../../../Types/PromoCodeTypes';
import { PromoCode } from '../../../DataService/CampainService';

type Props = {
  action: 'edit' | 'create';
  selectedPromoCode?: PromoCode | null;
  onSubmit: (data: PromoCode) => Promise<void>;
  onCancel: () => void;
};

const emptyPromoCode: PromoCode = {
  id: -1,
  code: '',
  description: '',
  discountType: DiscountType.Percentage,
  discountValue: 0,
  maxDiscountAmount: undefined,
  startDate: '',
  endDate: '',
  isActive: true,
  usageLimit: undefined,
  usageCount: 0,
  perUserLimit: undefined,
};

export const CampaignCodeForm = ({ action, selectedPromoCode, onSubmit, onCancel }: Props) => {
  const [promoCode, setPromoCode] = React.useState<PromoCode>(selectedPromoCode ?? emptyPromoCode);

  React.useEffect(() => {
    setPromoCode(selectedPromoCode ?? emptyPromoCode);
  }, [selectedPromoCode, action]);

  const updateField = (name: keyof PromoCode, value: string | number | boolean | undefined) => {
    setPromoCode(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="campaign-form-panel">
      <div className="campaign-form-header">
        <div>
          <h3>{action === 'edit' ? 'Redigera kampanjkod' : 'Skapa ny kampanjkod'}</h3>
          <p>All kampanjlogik samlad i en tabb, utan modalavbrott.</p>
        </div>
        <span className="admin-pill">{action === 'edit' ? 'Edit' : 'Create'}</span>
      </div>

      <div className="campaign-grid-2">
        <div className="campaign-space-y-2">
          <label htmlFor="code">Kampanjkod *</label>
          <input id="code" value={promoCode.code} onChange={e => updateField('code', e.target.value)} className="admin-campain-input" placeholder="WELCOME2024" />
        </div>
        <div className="campaign-space-y-2">
          <label htmlFor="discountType">Rabatt typ *</label>
          <Select
            value={String(promoCode.discountType)}
            onValueChange={(value) => updateField('discountType', Number(value) as DiscountType)}
          >
            <SelectTrigger className="admin-campain-select-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={String(DiscountType.Percentage)}>Procent (%)</SelectItem>
              <SelectItem value={String(DiscountType.FixedAmount)}>Fast belopp (kr)</SelectItem>
              <SelectItem value={String(DiscountType.FreeTrial)}>Prova på period</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="campaign-space-y-2">
        <label htmlFor="description">Beskrivning *</label>
        <textarea id="description" value={promoCode.description} onChange={e => updateField('description', e.target.value)} className="admin-campain-input admin-campain-input--textarea" placeholder="Beskrivning av kampanjen..." />
      </div>

      <div className="campaign-row-between">
        <div className="campaign-space-y-2">
          <label htmlFor="discountValue">{promoCode.discountType === DiscountType.Percentage ? 'Rabatt (%)' : 'Rabatt (kr)'}</label>
          <input id="discountValue" type="number" step="0.01" value={promoCode.discountValue} onChange={e => updateField('discountValue', Number(e.target.value))} className="admin-campain-input" />
        </div>
        <div className="campaign-space-y-2">
          <label htmlFor="maxDiscountAmount">Max rabatt (kr)</label>
          <input id="maxDiscountAmount" type="number" value={promoCode.maxDiscountAmount ?? ''} onChange={e => updateField('maxDiscountAmount', e.target.value === '' ? undefined : Number(e.target.value))} className="admin-campain-input" placeholder="Valfritt" />
        </div>
      </div>

      <div className="campaign-row-between">
        <div className="campaign-space-y-2">
          <label htmlFor="startDate">Startdatum *</label>
          <input id="startDate" type="date" value={promoCode.startDate} onChange={e => updateField('startDate', e.target.value)} className="admin-campain-input" />
        </div>
        <div className="campaign-space-y-2">
          <label htmlFor="endDate">Slutdatum *</label>
          <input id="endDate" type="date" value={promoCode.endDate ?? ''} onChange={e => updateField('endDate', e.target.value)} className="admin-campain-input" />
        </div>
      </div>

      <div className="campaign-grid-2">
        <div className="campaign-space-y-2">
          <label htmlFor="usageLimit">Maxantal användningar</label>
          <input id="usageLimit" type="number" value={promoCode.usageLimit ?? ''} onChange={e => updateField('usageLimit', e.target.value === '' ? undefined : Number(e.target.value))} className="admin-campain-input" placeholder="Obegränsat" />
        </div>
        <div className="campaign-space-y-2">
          <label htmlFor="perUserLimit">Max per användare</label>
          <input id="perUserLimit" type="number" value={promoCode.perUserLimit ?? ''} onChange={e => updateField('perUserLimit', e.target.value === '' ? undefined : Number(e.target.value))} className="admin-campain-input" placeholder="Obegränsat" />
        </div>
      </div>

      <div className="campaign-flex-row">
        <Switch id="isActive" checked={promoCode.isActive} onCheckedChange={checked => updateField('isActive', checked)} />
        <label htmlFor="isActive">Aktiv</label>
      </div>

      <div className="campaign-footer-buttons">
        <Button className="submit-button" onClick={onCancel} text="Avbryt" />
        <Button className="submit-button" onClick={() => onSubmit(promoCode)} text={action === 'edit' ? 'Uppdatera' : 'Skapa'} />
      </div>
    </div>
  );
};
